import React, { useState, useEffect } from "react";
import { useTheme } from "./context/ThemeContext";
import { useNavigate, useLocation } from "react-router-dom";
import AppRoutesDevelopment from "./AppRoutesDevelopment";

// Font Awesome
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSearch,
    faStar,
    faHistory,
    faCode,
    faChartLine,
    faPaintBrush,
    faBullhorn,
    faExchange,
    faShield,
    faLightbulb,
} from "@fortawesome/free-solid-svg-icons";

// Components
import tools from "./components/ToolCard/data/tools";
import Footer from "./components/footer/Footer";
import Header from "./components/header/Header";

const AppDevelopment = () => {
    const { darkMode } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [currentFilter, setCurrentFilter] = useState("all");
    const [currentTab, setCurrentTab] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [favorites, setFavorites] = useState(() => {
        const savedFavorites = localStorage.getItem('favorites');
        return savedFavorites ? JSON.parse(savedFavorites) : [];
    });
    const [recentTools, setRecentTools] = useState([]);

    useEffect(() => {
        localStorage.setItem('favorites', JSON.stringify(favorites));
    }, [favorites]);

    useEffect(() => {
        // Add scrollbar styles
        const style = document.createElement("style");
        style.textContent = `
      /* Custom Scrollbar */
      ::-webkit-scrollbar {
          width: 12px;
          height: 10px;
      }
    
      ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
      }
    
      ::-webkit-scrollbar-thumb {
          border-radius: 10px;
          background: #056644; /* Dark green */
          transition: background 0.3s;
      }
    
      ::-webkit-scrollbar-thumb:hover {
          background: #034f35; /* Slightly deeper green on hover */
      }
    
      /* Dark mode scrollbar */
      .dark ::-webkit-scrollbar-track {
          background:rgb(255, 255, 255);
      }
    
      .dark ::-webkit-scrollbar-thumb {
          background: #ffffff;
      }
    
      .dark ::-webkit-scrollbar-thumb:hover {
          background: #e2e2e2;
      }
    
      /* Smooth scrolling */
      html {
          scroll-behavior: smooth;
      }
    
      /* Scrollbar for Firefox */
      * {
          scrollbar-width: thin;
          scrollbar-color:rgb(3, 63, 42) #f1f1f1;
      }
    
      .dark * {
          scrollbar-color: #ffffff     #2d3748;
      }
    `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const isRootPath = location.pathname === "/";

    const handleSelectTool = (toolPath) => {
        const tool = tools.find((t) => t.path === toolPath);
        if (tool) {
            setRecentTools((prev) => {
                const newRecent = [
                    toolPath,
                    ...prev.filter((path) => path !== toolPath),
                ];
                return newRecent.slice(0, 4);
            });
            navigate(`/tool/${tool.id}`);
        }
    };

    const toggleFavorite = (toolPath) => {
        setFavorites(prev => {
            const newFavorites = prev.includes(toolPath)
                ? prev.filter(path => path !== toolPath)
                : [...prev, toolPath];
            localStorage.setItem('favorites', JSON.stringify(newFavorites));
            return newFavorites;
        });
    };

    const getFilteredTools = () => {
        let filteredTools = [...tools];

        if (currentFilter !== "all") {
            const filterMap = {
                dev: "Development",
                des: "Designing",
                mar: "Marketing",
                con: "Converter",
                seo: "SEO",
                sec: "Security",
                pro: "Productivity",
            };
            filteredTools = filteredTools.filter(
                (tool) => tool.category === filterMap[currentFilter]
            );
        }

        if (currentTab === "favorites") {
            filteredTools = filteredTools.filter((tool) =>
                favorites.includes(tool.path)
            );
        } else if (currentTab === "recent") {
            filteredTools = filteredTools.filter((tool) =>
                recentTools.includes(tool.path)
            );
        }

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filteredTools = filteredTools.filter((tool) =>
                tool.title.toLowerCase().includes(query)
            );
        }

        return filteredTools;
    };

    const filteredTools = getFilteredTools();

    const getCategoryClass = (category) => {
        const classes = {
            Designing:
                "bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-300",
            Development:
                "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300",
            Marketing:
                "bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-300",
            Converter:
                "bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300",
            Security:
                "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300",
            Productivity:
                "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
        };
        return (
            classes[category] ||
            "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
        );
    };

    // Check if we're on a tool page
    const isToolPage = location.pathname.startsWith('/tool/') || tools.some(tool => location.pathname === `/${tool.path}`);

    return (
        <div className="App flex flex-col min-h-screen relative bg-gray-100 dark:bg-gray-900 font-sans">
            {!isToolPage && <Header />}

            <main className="flex-grow">
                {isRootPath && (
                    <section className="container mx-auto px-2 py-8">
                        <div className="mb-8">
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                                <div className="relative flex-grow max-w-2xl">
                                    <input
                                        type="text"
                                        placeholder="Search tools..."
                                        value={searchQuery}
                                        onChange={(e) =>
                                            setSearchQuery(e.target.value)
                                        }
                                        className="search-box w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-400 transition bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                    />
                                    <FontAwesomeIcon
                                        icon={faSearch}
                                        className="absolute left-3 top-3.5 text-gray-400 dark:text-gray-500"
                                    />
                                </div>
                                <div className="flex space-x-1 overflow-x-auto pb-2">
                                    <button
                                        className={`filter-btn px-4 py-2 rounded-lg border transition whitespace-nowrap ${
                                            currentFilter === "all"
                                                ? "bg-indigo-100 border-indigo-300 dark:bg-indigo-900/50 dark:border-indigo-700 text-indigo-800 dark:text-white"
                                                : "bg-white border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 text-gray-800 dark:text-white"
                                        }`}
                                        onClick={() => setCurrentFilter("all")}
                                    >
                                        All Tools
                                    </button>
                                    <button
                                        className={`filter-btn px-4 py-2 rounded-lg border transition whitespace-nowrap ${
                                            currentFilter === "dev"
                                                ? "bg-indigo-100 border-indigo-300 dark:bg-indigo-900/50 dark:border-indigo-700 text-indigo-800 dark:text-white"
                                                : "bg-white border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 text-gray-800 dark:text-white"
                                        }`}
                                        onClick={() => setCurrentFilter("dev")}
                                    >
                                        <FontAwesomeIcon
                                            icon={faCode}
                                            className="mr-2 text-blue-500 dark:text-blue-400"
                                        />
                                        Development
                                    </button>
                                    <button
                                        className={`filter-btn px-4 py-2 rounded-lg border transition whitespace-nowrap ${
                                            currentFilter === "des"
                                                ? "bg-indigo-100 border-indigo-300 dark:bg-indigo-900/50 dark:border-indigo-700 text-indigo-800 dark:text-white"
                                                : "bg-white border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 text-gray-800 dark:text-white"
                                        }`}
                                        onClick={() => setCurrentFilter("des")}
                                    >
                                        <FontAwesomeIcon
                                            icon={faPaintBrush}
                                            className="mr-2 text-purple-500 dark:text-purple-400"
                                        />
                                        Designing
                                    </button>
                                    <button
                                        className={`filter-btn px-4 py-2 rounded-lg border transition whitespace-nowrap ${
                                            currentFilter === "mar"
                                                ? "bg-indigo-100 border-indigo-300 dark:bg-indigo-900/50 dark:border-indigo-700 text-indigo-800 dark:text-white"
                                                : "bg-white border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 text-gray-800 dark:text-white"
                                        }`}
                                        onClick={() => setCurrentFilter("mar")}
                                    >
                                        <FontAwesomeIcon
                                            icon={faBullhorn}
                                            className="mr-2 text-green-500 dark:text-green-400"
                                        />
                                        Marketing
                                    </button>
                                    <button
                                        className={`filter-btn px-4 py-2 rounded-lg border transition whitespace-nowrap ${
                                            currentFilter === "con"
                                                ? "bg-indigo-100 border-indigo-300 dark:bg-indigo-900/50 dark:border-indigo-700 text-indigo-800 dark:text-white"
                                                : "bg-white border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 text-gray-800 dark:text-white"
                                        }`}
                                        onClick={() => setCurrentFilter("con")}
                                    >
                                        <FontAwesomeIcon
                                            icon={faExchange}
                                            className="mr-2 text-orange-500 dark:text-orange-400"
                                        />
                                        Converter
                                    </button>
                                    <button
                                        className={`filter-btn px-4 py-2 rounded-lg border transition whitespace-nowrap ${
                                            currentFilter === "seo"
                                                ? "bg-indigo-100 border-indigo-300 dark:bg-indigo-900/50 dark:border-indigo-700 text-indigo-800 dark:text-white"
                                                : "bg-white border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 text-gray-800 dark:text-white"
                                        }`}
                                        onClick={() => setCurrentFilter("seo")}
                                    >
                                        <FontAwesomeIcon
                                            icon={faChartLine}
                                            className="mr-2 text-green-500 dark:text-green-400"
                                        />
                                        SEO
                                    </button>
                                    <button
                                        className={`filter-btn px-4 py-2 rounded-lg border transition whitespace-nowrap ${
                                            currentFilter === "sec"
                                                ? "bg-indigo-100 border-indigo-300 dark:bg-indigo-900/50 dark:border-indigo-700 text-indigo-800 dark:text-white"
                                                : "bg-white border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 text-gray-800 dark:text-white"
                                        }`}
                                        onClick={() => setCurrentFilter("sec")}
                                    >
                                        <FontAwesomeIcon
                                            icon={faShield}
                                            className="mr-2 text-red-500 dark:text-red-400"
                                        />
                                        Security
                                    </button>
                                    <button
                                        className={`filter-btn px-4 py-2 rounded-lg border transition whitespace-nowrap ${
                                            currentFilter === "pro"
                                                ? "bg-indigo-100 border-indigo-300 dark:bg-indigo-900/50 dark:border-indigo-700 text-indigo-800 dark:text-white"
                                                : "bg-white border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 text-gray-800 dark:text-white"
                                        }`}
                                        onClick={() => setCurrentFilter("pro")}
                                    >
                                        <FontAwesomeIcon
                                            icon={faLightbulb}
                                            className="mr-2 text-yellow-500 dark:text-yellow-400"
                                        />
                                        Productivity
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="mb-8 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex space-x-8">
                                <button
                                    className={`py-2 px-1 font-medium transition ${
                                        currentTab === "all"
                                            ? "tab-active text-indigo-600 dark:text-indigo-400"
                                            : "text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                                    }`}
                                    onClick={() => setCurrentTab("all")}
                                >
                                    All Tools
                                </button>
                                <button
                                    className={`py-2 px-1 font-medium transition ${
                                        currentTab === "favorites"
                                            ? "tab-active text-indigo-600 dark:text-indigo-400"
                                            : "text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                                    }`}
                                    onClick={() => setCurrentTab("favorites")}
                                >
                                    <FontAwesomeIcon
                                        icon={faStar}
                                        className="mr-2 text-yellow-400"
                                    />
                                    Favorites
                                </button>
                                <button
                                    className={`py-2 px-1 font-medium transition ${
                                        currentTab === "recent"
                                            ? "tab-active text-indigo-600 dark:text-indigo-400"
                                            : "text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                                    }`}
                                    onClick={() => setCurrentTab("recent")}
                                >
                                    <FontAwesomeIcon
                                        icon={faHistory}
                                        className="mr-2 text-gray-400"
                                    />
                                    Recent
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {filteredTools.length === 0 ? (
                                <div className="col-span-full text-center py-16 empty-state">
                                    <FontAwesomeIcon
                                        icon={faSearch}
                                        className="text-5xl text-gray-300 dark:text-gray-700 mb-4"
                                    />
                                    <h3 className="text-xl font-medium text-gray-500 dark:text-gray-400">
                                        No tools found
                                    </h3>
                                    <p className="text-gray-400 dark:text-gray-500 mt-2">
                                        Try adjusting your search or filter to
                                        find what you're looking for.
                                    </p>
                                </div>
                            ) : (
                                filteredTools.map((tool, index) => (
                                    <div
                                        key={tool.path}
                                        className="tool-card bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition flex flex-col"
                                        style={{
                                            animation: `toolSlideIn 0.3s ease-out ${
                                                index * 0.1
                                            }s forwards`,
                                            opacity: 0,
                                        }}
                                    >
                                        <div className="p-6 flex flex-col h-full">
                                            <div className="flex items-start justify-between">
                                                <div
                                                    className={`p-3 rounded-lg ${getCategoryClass(
                                                        tool.category
                                                    )}`}
                                                >
                                                    <div className="text-xl">
                                                        {tool.icon}
                                                    </div>
                                                </div>
                                                <button
                                                    className={`favorite-btn transition ${
                                                        favorites.includes(
                                                            tool.path
                                                        )
                                                            ? "text-yellow-400"
                                                            : "text-gray-300 hover:text-yellow-400"
                                                    }`}
                                                    onClick={() =>
                                                        toggleFavorite(
                                                            tool.path
                                                        )
                                                    }
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faStar}
                                                    />
                                                </button>
                                            </div>

                                            <div className="mt-4 flex-grow">
                                                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                                                    {tool.title}
                                                </h3>
                                                <p className="mt-2 text-gray-600 dark:text-gray-300">
                                                    {tool.description}
                                                </p>
                                            </div>

                                            <div className="mt-4 flex justify-between items-center">
                                                <span
                                                    className={`text-xs px-2 py-1 rounded-full ${getCategoryClass(
                                                        tool.category
                                                    )}`}
                                                >
                                                    {tool.category}
                                                </span>
                                                <button
                                                    className="use-btn bg-[#70E4A8] hover:bg-[#5DCA95] text-white px-4 py-2 rounded-lg text-sm font-medium transition"
                                                    onClick={() =>
                                                        handleSelectTool(tool.path)
                                                    }
                                                >
                                                    Use Tool
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </section>
                )}

                <AppRoutesDevelopment
                    favorites={favorites}
                    onToggleFavorite={toggleFavorite}
                />
            </main>

            {!isToolPage && <Footer />}
        </div>
    );
};

export default AppDevelopment;












